import React, { useState } from "react";
import { Flex, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useMembership from "@/shared/hooks/useMembership";
import PathPickMembershipPlanCompareSection from "@/features/pathpick/features/membership/components/plan-compare-section/PathPickMembershipPlanCompareSection";
import { MEMBERSHIP_PLANS } from "@/features/pathpick/features/membership/constants";
import { MembershipBillingCycle } from "@/features/pathpick/features/membership/types";
import PathPickStartGuideCard from "@/features/pathpick/features/start-guide/components/PathPickStartGuideCard";

interface Props {
  onPrev: () => void;
  onNext: () => void;
}

/**
 * 멤버십과 이용 한도.
 *
 * 플랜 카드·가격·한도는 운영 멤버십 화면과 같은 `MEMBERSHIP_PLANS` 단일 소스를 쓰는
 * `PathPickMembershipPlanCompareSection` 을 그대로 감싼다 — 가이드에서 숫자를 따로
 * 하드코딩하면 두 화면이 갈라진다(전달서 §15).
 * 가이드는 한도를 알려 주기만 한다 — 플랜별 CTA 는 띄우지 않고 결제·상담도 접수하지 않는다.
 */
function PathPickStartGuideMembershipSection({ onPrev, onNext }: Props) {
  const { t } = useI18nHelper();
  const { planKey, isFirstPathPickPayment } = useMembership();
  const [billingCycle, setBillingCycle] =
    useState<MembershipBillingCycle>("monthly");

  const currentPlan = MEMBERSHIP_PLANS.find((plan) => plan.id === planKey);

  return (
    <Flex flexDirection={"column"} gap={"16px"}>
      <PathPickStartGuideCard
        header={
          <Flex flexDirection={"column"} gap={"6px"}>
            <Text
              textStyle={"body-lg-bold"}
              fontSize={"22px"}
              color={"#1D1D1B"}
            >
              {t("path_pick_guide:membership.title")}
            </Text>
            {/* 중복 설명 박스와 중복 소개 문구는 넣지 않는다(전달서 §15). */}
            <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
              {t("path_pick_guide:membership.description")}
            </Text>
          </Flex>
        }
        headerProps={{
          paddingBottom: "0px",
        }}
      >
        {/* 이 탭은 읽기만 하는 화면이라 단계 설명이 없다 — 이동 버튼만 카드 우측 하단에 둔다.
            앞뒤가 모두 다른 탭이므로 두 버튼 다 `단계` 라벨이다. */}
        <Flex gap={"8px"} justifyContent={"flex-end"}>
          <AppButton variant={"normal"} onClick={onPrev}>
            {t("path_pick_guide:nav.prev_step")}
          </AppButton>
          <AppButton variant={"primary"} onClick={onNext}>
            {t("path_pick_guide:nav.next_step")}
          </AppButton>
        </Flex>
      </PathPickStartGuideCard>

      <PathPickMembershipPlanCompareSection
        billingCycle={billingCycle}
        onChangeBillingCycle={setBillingCycle}
        currentPlan={currentPlan}
        isFirstSubscription={isFirstPathPickPayment}
        // 가이드에서는 플랜을 고르는 자리가 아니다 — 시작하기·업/다운그레이드·상담하기를 모두 감춘다.
        hasCta={false}
        descriptionOverride={t(
          "path_pick_guide:membership.compare_description"
        )}
      />
    </Flex>
  );
}

export default PathPickStartGuideMembershipSection;
